import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  useTheme
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

interface DailyBarChartProps {
  chartTitle: string;
  data: Record<string, any>[]; // Array of { date, [workflowName]: number }
  colorMap: Record<string, string>; // workflowName => color
  year?: number;
  monthIdx?: number; // 0-indexed
  headerAction?: React.ReactNode;
  loading?: boolean;
}

const DailyBarChart: React.FC<DailyBarChartProps> = ({
  chartTitle,
  data,
  colorMap,
  year,
  monthIdx,
  headerAction,
  loading = false
}) => {
  const theme = useTheme();

  const workflowKeys =
    data.length > 0
      ? Array.from(
          new Set(
            data.flatMap((entry) =>
              Object.keys(entry).filter((key) => key !== 'date')
            )
          )
        )
      : Object.keys(colorMap);

  // Default date is set depending on data input if and only if the year and monthIdx are not provided
  const defaultDate =
    data && data.length > 0 && data[0].date
      ? new Date(data[0].date)
      : new Date();
  const targetYear =
    typeof year === 'number' ? year : defaultDate.getFullYear();
  const targetMonth =
    typeof monthIdx === 'number' ? monthIdx : defaultDate.getMonth(); // 0-indexed

  const daysInMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
  const allDates = Array.from({ length: daysInMonth }, (_, i) => {
    const date = new Date(targetYear, targetMonth, i + 1);
    return date.toISOString().split('T')[0]; // 'YYYY-MM-DD'
  });

  const mergedData = allDates.map((dateStr) => {
    const found = data.find((entry) => entry.date === dateStr);
    if (found) return { baseline: 0, ...found };

    return {
      date: dateStr,
      baseline: 0,
      ...Object.fromEntries(
        Object.keys(colorMap).map((workflow) => [workflow, 0])
      )
    };
  });
  const grandTotal = mergedData.reduce((sum, entry: any) => {
    return (
      sum +
      Object.keys(entry).reduce((subSum, key) => {
        if (key !== 'date' && key !== 'baseline') {
          return subSum + (typeof entry[key] === 'number' ? entry[key] : 0);
        }
        return subSum;
      }, 0)
    );
  }, 0);

  return (
    <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            marginBottom: 2
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            {chartTitle} (Total: {grandTotal.toLocaleString('en-US')})
          </Typography>
          {headerAction}
        </Box>
        <Box
          sx={{
            width: '100%',
            height: { xs: 250, md: 300 },
            overflowX: 'auto',
            overflowY: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {loading ? (
            <CircularProgress />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={mergedData} margin={{ right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  interval={5}
                  tickFormatter={(d) => {
                    const [, month, day] = d.split('-');
                    return `${month}/${day}`;
                  }}
                />
                <YAxis
                  domain={[
                    0,
                    (dataMax: number) =>
                      dataMax > 0 ? Math.ceil(dataMax * 1.1) : 100
                  ]}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload || payload.length === 0)
                      return null;

                    const total = (payload as Array<{ value: number }>).reduce(
                      (sum, p) => sum + p.value,
                      0
                    );

                    return (
                      <Box
                        sx={{
                          backgroundColor: 'white',
                          border: '1px solid #ccc',
                          borderRadius: 1,
                          p: 1,
                          boxShadow: 2
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold">
                          {label}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Total: {total.toLocaleString('en-US')}
                        </Typography>
                        {total > 0 &&
                          payload
                            .filter((entry) => entry.name !== 'baseline')
                            .map((entry) => (
                              <Box
                                key={entry.name}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ mt: 0.25 }}
                              >
                                <Box display="flex" alignItems="center" mr={1}>
                                  <Box
                                    sx={{
                                      width: 10,
                                      height: 10,
                                      backgroundColor: entry.color,
                                      borderRadius: '2px',
                                      mr: 1
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    color={entry.color}
                                  >
                                    {entry.name}
                                  </Typography>
                                </Box>
                                <Typography variant="body2">
                                  {entry.value?.toLocaleString('en-US')}
                                </Typography>
                              </Box>
                            ))}
                      </Box>
                    );
                  }}
                  isAnimationActive={false}
                />
                {workflowKeys.map((workflowName) => (
                  <Bar
                    key={workflowName}
                    dataKey={workflowName}
                    name={workflowName}
                    stackId="stack"
                    fill={colorMap[workflowName] || theme.palette.grey[400]}
                    radius={[1, 1, 0, 0]}
                    stroke={theme.palette.grey[300]}
                    strokeWidth={0.5}
                  />
                ))}
                {/* Baseline bar to handle rendering of tooltip incase data is completely empty */}
                <Bar
                  dataKey="baseline"
                  stackId="stack"
                  fill="transparent"
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default DailyBarChart;
